import { Handle, Position } from '@xyflow/react';
import { Stack, TextInput, Select, Textarea, NumberInput, Button, Group, Paper, ActionIcon } from '@mantine/core';
import { IconApi, IconPlus, IconTrash } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import {
  httpRequestWidgetCore,
  type HttpRequestConfig,
  type HttpHeader,
} from '@legacy-shared/widgets/core/httpRequest';

export { type HttpRequestConfig, type HttpHeader } from '@legacy-shared/widgets/core/httpRequest';

const methodOptions = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

function HttpRequestNode({ id, data, selected }: WidgetNodeProps<HttpRequestConfig>) {
  const preview = data.config.url ? `${data.config.method} ${new URL(data.config.url).hostname}` : 'No URL configured';

  return (
    <WidgetNodeShell
      icon={IconApi}
      title={data.label || 'HTTP Request'}
      subtitle={preview}
      configured={!!data.config.url}
      errors={data.errors}
      selected={selected}
      color="pink"
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="success" label="Success" offset="50%" nodeId={id} />
      <LabeledHandle type="source" position="right" id="error" label="Error" offset="50%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function HttpRequestConfigPanel({ config, onChange }: ConfigPanelProps<HttpRequestConfig>) {
  const addHeader = () => {
    onChange({
      ...config,
      headers: [...config.headers, { key: '', value: '' }],
    });
  };

  const updateHeader = (index: number, updates: Partial<HttpHeader>) => {
    onChange({ ...config, headers: config.headers.map((h, i) => (i === index ? { ...h, ...updates } : h)) });
  };

  const removeHeader = (index: number) => {
    const newHeaders = config.headers.filter((_, i) => i !== index);
    onChange({ ...config, headers: newHeaders });
  };

  return (
    <Stack gap="md">
      <TextInput
        label="URL"
        description="The API endpoint to call"
        placeholder="https://api.example.com/endpoint"
        value={config.url}
        onChange={(e) => onChange({ ...config, url: e.target.value })}
      />
      <Select
        label="Method"
        description="HTTP request method"
        value={config.method}
        onChange={(value) => onChange({ ...config, method: value || 'GET' })}
        data={methodOptions}
      />
      <TextInput
        label="Response Variable"
        description="Variable name to store the response"
        placeholder="httpResponse"
        value={config.responseVariable}
        onChange={(e) => onChange({ ...config, responseVariable: e.target.value })}
      />
      <NumberInput
        label="Timeout (seconds)"
        description="Maximum time to wait for response"
        value={config.timeout}
        onChange={(value) => onChange({ ...config, timeout: typeof value === 'number' ? value : 10 })}
        min={1}
        max={30}
      />

      <div>
        <Group justify="space-between" mb="xs">
          <div style={{ fontWeight: 500, fontSize: '14px' }}>Headers</div>
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addHeader}>
            Add
          </Button>
        </Group>
        <Stack gap="xs">
          {config.headers.map((header, index) => (
            <Paper key={index} p="xs" withBorder>
              <Group gap="xs">
                <TextInput
                  size="xs"
                  placeholder="Header name"
                  value={header.key}
                  onChange={(e) => updateHeader(index, { key: e.target.value })}
                  style={{ flex: 1 }}
                />
                <TextInput
                  size="xs"
                  placeholder="Value"
                  value={header.value}
                  onChange={(e) => updateHeader(index, { value: e.target.value })}
                  style={{ flex: 2 }}
                />
                <ActionIcon color="red" variant="subtle" onClick={() => removeHeader(index)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Paper>
          ))}
        </Stack>
      </div>

      {['POST', 'PUT', 'PATCH'].includes(config.method) && (
        <Textarea
          label="Request Body"
          description="JSON body to send with the request"
          placeholder='{"key": "value"}'
          value={config.body || ''}
          onChange={(e) => onChange({ ...config, body: e.target.value || undefined })}
          minRows={4}
          autosize
        />
      )}
    </Stack>
  );
}

export const httpRequestWidget: WidgetDefinition<HttpRequestConfig> = {
  ...httpRequestWidgetCore,
  icon: 'IconApi',
  variables: httpRequestWidgetCore.variables,

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [
      { id: 'success', type: 'success', label: 'Success', position: 'bottom' },
      { id: 'error', type: 'failure', label: 'Error', position: 'right' },
    ],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: HttpRequestNode,
    configPanel: HttpRequestConfigPanel,
    preview: (config) => {
      if (!config.url) return 'No URL';
      try {
        return `${config.method} ${new URL(config.url).hostname}`;
      } catch {
        return config.method;
      }
    },
  },

  validation: {
    ...httpRequestWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
